// ecommerce-frontend/src/pages/ProductDetail.jsx
// ✅ VERSION AMÉLIORÉE: UX optimale + Miniatures visibles + Stock temps réel + Style harmonisé (OrdersPage)

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, Heart, Plus, Minus, Star, Shield, Truck,
  Mail, Phone, MessageCircle, Award, ArrowLeft, Home, AlertCircle,
  ChevronLeft, ChevronRight, ZoomIn, Package, Clock, RefreshCw,
  Search, TrendingUp, CheckCircle2, Ban, Loader, Eye, Download, MessageSquare
} from 'lucide-react';
import { productsAPI, cartAPI, authAPI } from '../services/api';
import axios from 'axios';

const API_URL = 'http://localhost:8000/api';

const favoritesAPI = {
  checkFavorite: async (productId) => {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    
    try {
      const response = await axios.get(`${API_URL}/products/favorites/check/`, {
        params: { product_id: productId },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.is_favorite;
    } catch (error) {
      console.error('Erreur check favoris:', error);
      return false;
    }
  },

  toggleFavorite: async (productId) => {
    const token = localStorage.getItem('access_token');
    if (!token) throw new Error('Non authentifié');
    
    const response = await axios.post(
      `${API_URL}/products/favorites/toggle/`,
      { product_id: productId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  }
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [cartQuantity, setCartQuantity] = useState(0);
  const [availableStock, setAvailableStock] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);
  const [imageZoom, setImageZoom] = useState(false);
  const [notification, setNotification] = useState(null);
  const user = authAPI.getCurrentUser();

  useEffect(() => {
    loadProduct();
    loadCartQuantity();
    checkFavorite();
  }, [id]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const loadProduct = async () => {
    setLoading(true);
    try {
      const data = await productsAPI.getProduct(id);
      setProduct(data || null);
      setAvailableStock(data?.available_stock ?? data?.stock ?? 0);
    } catch (error) {
      console.error('Erreur chargement produit:', error);
      showNotification('Produit introuvable', 'error');
      // On évite la redirection immédiate si le produit est null, pour afficher le message de chargement/erreur
      // navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadCartQuantity = async () => {
    if (!authAPI.isAuthenticated()) return;
    
    try {
      const cart = await cartAPI.getCart();
      const item = cart.items?.find(item => item.product.id === parseInt(id));
      setCartQuantity(item?.quantity || 0);
    } catch (error) {
      console.error('Erreur chargement panier:', error);
    }
  };

  const checkFavorite = async () => {
    if (!authAPI.isAuthenticated()) {
      setIsFavorite(false);
      return;
    }

    try {
      const isFav = await favoritesAPI.checkFavorite(id);
      setIsFavorite(isFav);
    } catch (error) {
      console.error('Erreur vérification favoris:', error);
      setIsFavorite(false);
    }
  };

  const toggleFavorite = async () => {
    if (!authAPI.isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      const result = await favoritesAPI.toggleFavorite(product.id);
      setIsFavorite(result.is_favorite);
      showNotification(
        result.is_favorite ? '❤️ Ajouté aux favoris' : 'Retiré des favoris',
        'success'
      );
    } catch (error) {
      console.error('Erreur toggle favoris:', error);
      showNotification('Erreur lors de la modification des favoris', 'error');
    }
  };

  const addToCart = async () => {
    if (!authAPI.isAuthenticated()) {
      navigate('/login');
      return;
    }

    if (quantity > availableStock) {
      showNotification(`Stock insuffisant ! Il reste seulement ${availableStock} unité(s) disponible(s).`, 'error');
      return;
    }

    setAddingToCart(true);
    try {
      await cartAPI.addToCart(product.id, quantity);
      await loadProduct();
      await loadCartQuantity();
      setQuantity(1);
      showNotification(`✓ ${quantity} × ${product.name} ajouté au panier !`, 'success');
    } catch (error) {
      console.error('Erreur ajout panier:', error);
      showNotification(
        error.response?.data?.error || 'Erreur lors de l\'ajout au panier',
        'error'
      );
      await loadProduct();
    } finally {
      setAddingToCart(false);
    }
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity > availableStock) {
      showNotification(`Stock insuffisant ! Maximum: ${availableStock}`, 'error');
      setQuantity(Math.max(1, availableStock));
      return;
    }
    setQuantity(Math.max(1, newQuantity));
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/600x600?text=Pas+d%27image';
    if (imagePath.startsWith('http')) return imagePath;
    const baseUrl = 'http://localhost:8000';
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${baseUrl}${cleanPath}`;
  };

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      // ✅ STYLE HARMONISÉ : Fond et animation de chargement
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-orange-500/30 border-t-orange-500 mx-auto mb-6"></div>
            <div className="absolute inset-0 rounded-full h-20 w-20 border-4 border-orange-500/10 blur-xl mx-auto animate-pulse"></div>
          </div>
          <div className="text-xl text-gray-300 font-medium animate-pulse">Chargement du produit...</div>
        </div>
      </div>
    );
  }

  if (!product) {
     return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-8">
            <div className="bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl p-16 text-center border border-white/20 max-w-lg">
                <AlertCircle size={48} className="text-red-400 mx-auto mb-6" />
                <h2 className="text-3xl font-black text-white mb-3">Produit Introuvable</h2>
                <p className="text-gray-400 mb-8">
                    Désolé, l'article que vous recherchez n'est pas disponible ou l'URL est incorrecte.
                </p>
                <button 
                  onClick={() => navigate('/')} 
                  className="group/btn relative overflow-hidden inline-flex items-center space-x-2"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-pink-600 opacity-0 group-hover/btn:opacity-100 transition-opacity rounded-2xl"></div>
                  <div className="relative bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-2xl font-bold shadow-2xl transform group-hover/btn:scale-105 transition-all flex items-center space-x-2">
                    <Home size={20} />
                    <span>Retour à l'accueil</span>
                  </div>
                </button>
            </div>
        </div>
    );
  }

  const images = Array.isArray(product.images) && product.images.length > 0
    ? [product.image, ...product.images.filter(img => img && img.image).map(img => img.image)]
    : product.image ? [product.image] : [];

  const isOutOfStock = availableStock <= 0;
  const isLowStock = availableStock > 0 && availableStock <= 5;

  return (
    // ✅ STYLE HARMONISÉ : Fond et animations
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Bulle orange/rose */}
        <div className="absolute top-0 -left-20 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        {/* Bulle mauve/bleu */}
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-gradient-to-tl from-purple-500/15 to-blue-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        {/* Bulle centrale */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '500ms' }}></div>
      </div>

      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-24 right-4 z-[100] animate-slide-in-right ${
          notification.type === 'success' ? 'bg-green-500/90' : 'bg-red-500/90'
        } backdrop-blur-xl text-white px-6 py-4 rounded-2xl shadow-2xl border border-white/20 max-w-md`}>
          <div className="flex items-center space-x-3">
            {notification.type === 'success' ? (
              <CheckCircle2 size={20} className="text-white"/>
            ) : (
              <AlertCircle size={20} />
            )}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      {/* Header Glass */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/5 border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate(-1)}
                className="group p-2.5 text-white/70 hover:text-purple-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <ArrowLeft size={20} className="relative z-10" />
              </button>
              <div 
                className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent cursor-pointer hover:scale-105 transition-transform"
                onClick={() => navigate('/')}
              >
                MonMarket
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={loadProduct}
                className="group p-2.5 text-white/70 hover:text-orange-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden transform hover:rotate-180"
                title="Actualiser"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <RefreshCw size={20} className="relative z-10" />
              </button>
              <button 
                onClick={() => navigate('/')}
                className="group p-2.5 text-white/70 hover:text-orange-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <Home size={20} className="relative z-10" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 relative z-10">
        <div className="bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden border border-white/20 group">
          
          {/* Section Contact Vendeur */}
          {(product.vendor_email || product.vendor_phone) && (
            <div className="bg-gradient-to-r from-white/5 to-white/10 p-6 md:p-8 border-b border-white/10">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                <Award size={20} className="text-orange-400" />
                <span>Vendeur certifié</span>
              </h2>
              
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-center space-x-4">
                  {product.vendor_profile_photo ? (
                    <img 
                      src={getImageUrl(product.vendor_profile_photo)}
                      alt={product.vendor_name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-orange-500/50 shadow-lg"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                      {(product.vendor_name || 'V')[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="text-lg font-bold text-white">{product.vendor_name || 'Vendeur Certifié'}</div>
                    <div className="flex items-center space-x-1 mt-1">
                      {/* Note Fixe pour l'exemple, à remplacer par la note réelle */}
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          size={14} 
                          className={i < 4 ? 'text-yellow-500 fill-current' : 'text-gray-600'}
                        />
                      ))}
                      <span className="text-sm text-gray-400 ml-2">(4.8/5 - 120 avis)</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  {product.vendor_email && (
                    <a
                      href={`mailto:${product.vendor_email}`}
                      className="flex items-center justify-center space-x-2 bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white py-2.5 px-4 rounded-xl transition-all transform hover:scale-105 border border-white/10 shadow-lg"
                    >
                      <Mail size={18} />
                      <span className="text-sm font-medium">Email</span>
                    </a>
                  )}
                  {product.vendor_phone && (
                    <>
                      <button
                        onClick={() => {
                          const phone = product.vendor_phone.replace(/[^0-9]/g, '');
                          window.open(`https://wa.me/${phone}`, '_blank');
                        }}
                        className="flex items-center justify-center space-x-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 hover:text-green-300 py-2.5 px-4 rounded-xl transition-all transform hover:scale-105 border border-green-500/30 shadow-lg"
                      >
                        <MessageSquare size={18} />
                        <span className="text-sm font-medium">WhatsApp</span>
                      </button>
                      <a
                        href={`tel:${product.vendor_phone}`}
                        className="flex items-center justify-center space-x-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 hover:text-blue-300 py-2.5 px-4 rounded-xl transition-all transform hover:scale-105 border border-blue-500/30 shadow-lg"
                      >
                        <Phone size={18} />
                        <span className="text-sm font-medium">Appeler</span>
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Section Détails et Images */}
          <div className="grid lg:grid-cols-2">
            
            {/* Section Image - Gauche */}
            <div className="bg-black/10 p-6 md:p-12 flex flex-col border-b lg:border-b-0 lg:border-r border-white/10">
              
              {/* Image principale avec zoom */}
              <div className="relative flex-1 flex items-center justify-center mb-6 group">
                <div className="relative">
                  <img 
                    src={getImageUrl(images[selectedImage] || product.image)}
                    alt={product.name}
                    className={`max-w-full max-h-[500px] object-contain drop-shadow-2xl transition-all duration-300 rounded-xl ${
                      imageZoom ? 'scale-150 cursor-zoom-out' : 'cursor-zoom-in hover:scale-105'
                    }`}
                    onClick={() => setImageZoom(!imageZoom)}
                  />
                  
                  {/* Bouton Zoom */}
                  <button
                    onClick={() => setImageZoom(!imageZoom)}
                    className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ZoomIn size={20} />
                  </button>

                  {/* Navigation images (si plusieurs) */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-md text-white p-3 rounded-full hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100 transform hover:scale-110"
                      >
                        <ChevronLeft size={24} />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-md text-white p-3 rounded-full hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100 transform hover:scale-110"
                      >
                        <ChevronRight size={24} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Miniatures améliorées */}
              {images.length > 1 && (
                <div className="relative">
                  <div className="text-xs text-gray-400 mb-2 text-center">
                    {selectedImage + 1} / {images.length} images
                  </div>
                  <div className="flex justify-center space-x-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent px-4">
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImage(idx)}
                        className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all backdrop-blur-md transform hover:scale-110 ${
                          selectedImage === idx 
                            ? 'border-orange-500 shadow-[0_0_20px_rgba(249,115,22,0.5)] scale-110 ring-2 ring-orange-400/50' 
                            : 'border-white/20 hover:border-white/40 opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img 
                          src={getImageUrl(img)} 
                          alt={`Vue ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Section Détails - Droite */}
            <div className="p-6 md:p-12 flex flex-col text-gray-100">
              {/* En-tête */}
              <div className="mb-6">
                <div className="text-sm font-semibold text-orange-400 uppercase tracking-wider mb-2 flex items-center space-x-2">
                  <Package size={16} />
                  <span>{product.category_name || 'Produit'}</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                  {product.name}
                </h1>
                
                {/* Notes et avis */}
                <div className="flex items-center space-x-4 flex-wrap gap-2">
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={18} 
                        className={i < 4 ? 'text-yellow-500 fill-current' : 'text-gray-600'}
                      />
                    ))}
                  </div>
                  <span className="text-gray-400">4.8/5</span>
                  <span className="text-gray-500">•</span>
                  <span className="text-gray-400">120 avis</span>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <p className="text-gray-300 leading-relaxed whitespace-pre-line text-sm max-h-32 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                  {product.description}
                </p>
              </div>

              {/* Badges de statut */}
              <div className="flex items-center flex-wrap gap-3 mb-6 pb-6 border-b border-white/10">
                <div className={`flex items-center space-x-2 px-4 py-2 rounded-full border transition-all ${
                  isOutOfStock 
                    ? 'bg-red-500/20 text-red-400 border-red-500/30 shadow-[0_0_15px_rgba(239,68,68,0.3)]' 
                    : isLowStock 
                      ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30 shadow-[0_0_15px_rgba(234,179,8,0.3)]'
                      : 'bg-green-500/20 text-green-400 border-green-500/30 shadow-[0_0_15px_rgba(34,197,94,0.3)]'
                }`}>
                  <div className={`w-2.5 h-2.5 rounded-full ${
                    isOutOfStock ? 'bg-red-500' : isLowStock ? 'bg-yellow-500 animate-pulse' : 'bg-green-500 animate-pulse'
                  }`}></div>
                  <span className="font-semibold text-sm">
                    {isOutOfStock 
                      ? 'Rupture de stock' 
                      : `Stock: ${availableStock} unité${availableStock > 1 ? 's' : ''}`
                    }
                  </span>
                </div>
                
                {cartQuantity > 0 && (
                  <div className="flex items-center space-x-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full border border-blue-500/30 shadow-[0_0_15px_rgba(59,130,246,0.3)]">
                    <ShoppingCart size={16} />
                    <span className="font-semibold text-sm">{cartQuantity} dans le panier</span>
                  </div>
                )}

                {!isOutOfStock && (
                  <div className="flex items-center space-x-2 bg-purple-500/20 text-purple-400 px-4 py-2 rounded-full border border-purple-500/30">
                    <Clock size={16} />
                    <span className="font-medium text-sm">Livraison rapide</span>
                  </div>
                )}
              </div>

              {/* Alerte stock faible */}
              {isLowStock && (
                <div className="mb-6 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start backdrop-blur-sm shadow-lg">
                  <AlertCircle size={20} className="text-yellow-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-yellow-200 text-sm font-medium">
                      ⚠️ Stock limité !
                    </p>
                    <p className="text-yellow-300/80 text-xs mt-1">
                      Plus que {availableStock} unité{availableStock > 1 ? 's' : ''} disponible{availableStock > 1 ? 's' : ''}. Commandez vite !
                    </p>
                  </div>
                </div>
              )}

              {/* Prix */}
              <div className="mb-8">
                <div className="flex items-baseline space-x-4 flex-wrap">
                  <div className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
                    {product.price?.toLocaleString()} CFA
                  </div>
                  {product.old_price && (
                    <>
                      <div className="text-xl text-gray-500 line-through">
                        {product.old_price?.toLocaleString()} CFA
                      </div>
                      <div className="bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm font-bold border border-red-500/30">
                        -{Math.round(((product.old_price - product.price) / product.old_price) * 100)}%
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Sélecteur de Quantité */}
              {!isOutOfStock && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
                      Quantité
                    </div>
                    <div className="text-sm text-orange-400 font-medium">
                      Maximum: {availableStock}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center bg-white/5 p-1.5 rounded-xl border border-white/10 backdrop-blur-sm shadow-lg">
                      <button
                        onClick={() => handleQuantityChange(quantity - 1)}
                        disabled={quantity <= 1}
                        className="w-12 h-12 rounded-lg hover:bg-orange-500/20 text-white flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed transform hover:scale-110 active:scale-95"
                      >
                        <Minus size={20} />
                      </button>
                      <input
                        type="number"
                        value={quantity}
                        onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                        min="1"
                        max={availableStock}
                        className="w-20 text-center text-2xl font-bold text-white bg-transparent border-none focus:outline-none font-mono"
                      />
                      <button
                        onClick={() => handleQuantityChange(quantity + 1)}
                        disabled={quantity >= availableStock}
                        className="w-12 h-12 rounded-lg hover:bg-orange-500/20 text-white flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed transform hover:scale-110 active:scale-95"
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                    <div className="text-sm text-gray-400">
                      Total: <span className="text-orange-400 font-bold text-lg">
                        {(product.price * quantity).toLocaleString()} CFA
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Boutons d'action */}
              <div className="flex space-x-4 mb-8">
                <button
                  onClick={addToCart}
                  disabled={isOutOfStock || addingToCart}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-4 rounded-xl font-bold text-lg transition-all shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 relative overflow-hidden group/add"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -translate-x-full group-hover/add:translate-x-full transition-transform duration-1000"></div>
                  <span className="relative flex items-center justify-center space-x-2">
                    {addingToCart ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Ajout...</span>
                      </>
                    ) : (
                      <>
                        <ShoppingCart size={22} />
                        <span>{isOutOfStock ? 'Rupture de stock' : 'Ajouter au panier'}</span>
                      </>
                    )}
                  </span>
                </button>
                
                <button
                  onClick={toggleFavorite}
                  className={`w-16 h-16 rounded-xl border-2 transition-all flex items-center justify-center backdrop-blur-md transform hover:scale-110 active:scale-95 ${
                    isFavorite 
                      ? 'bg-red-500/20 border-red-500/50 text-red-500 shadow-[0_0_20px_rgba(239,68,68,0.4)]' 
                      : 'bg-white/5 border-white/10 hover:border-white/30 text-gray-400 hover:text-red-400'
                  }`}
                  title={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                >
                  <Heart size={26} className={`${isFavorite ? 'fill-current animate-pulse' : ''} transition-all`} />
                </button>
              </div>

              {/* Garanties */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3 text-sm text-gray-300 bg-white/5 p-4 rounded-xl border border-white/10 hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Shield size={20} className="text-green-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-white">Garantie constructeur</div>
                    <div className="text-xs text-gray-400">Protection 2 ans incluse</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 text-sm text-gray-300 bg-white/5 p-4 rounded-xl border border-white/10 hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Truck size={20} className="text-blue-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-white">Livraison express</div>
                    <div className="text-xs text-gray-400">Partout en Côte d'Ivoire sous 24-48h</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 text-sm text-gray-300 bg-white/5 p-4 rounded-xl border border-white/10 hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Award size={20} className="text-purple-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-white">Produit certifié</div>
                    <div className="text-xs text-gray-400">100% authentique et vérifié</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}